import { useEffect, useState } from "react";
import { Command } from "cmdk";
import {
  CalendarClock,
  Car,
  ListTodo,
  Loader2,
  Search,
  Settings,
  Target,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  cn,
  formatDateTimeInZone,
  humanize,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { tasksApi } from "../api/tasks";
import { vehiclesApi } from "../api/vehicles";
import { contactDisplayName } from "../lib/contact";
import { useRooftopZone } from "../lib/rooftop-zone";
import { useHotkeys } from "../lib/hotkeys";

const SETTINGS_DESTINATIONS = [
  { label: "Assistant", detail: "Automation settings", to: "/settings" },
  { label: "Business hours", detail: "Dealership hours", to: "/settings/business-hours" },
  { label: "Channels", detail: "Connected messaging channels", to: "/settings/channels" },
  { label: "Phone and SMS", detail: "Telephony and A2P", to: "/settings/telephony" },
  { label: "Team", detail: "People and permissions", to: "/settings/team" },
  { label: "Notifications", detail: "Personal notification settings", to: "/settings/notifications" },
] as const;

/**
 * Global search — ⌘K / Ctrl K palette, built on cmdk.
 * Searches the operational records people move between most often using
 * their existing server-authoritative endpoints.
 *
 * cmdk owns the keyboard/aria model (roving listbox: ↑↓ move the active
 * option, ↵ fires its onSelect, Esc closes) so this component never
 * hand-rolls arrow handling. Server filters, so `shouldFilter={false}`.
 *
 * The palette rides <DialogContent bare> — no built-in chrome padding, no
 * stray X — and top-anchors on desktop (sm:top-[18%]) instead of dead
 * centering so the box grows DOWNWARD as results arrive rather than jumping
 * to stay centered. Below sm: it's the shared bottom sheet.
 *
 * Rendered twice by AppShell — the "desktop" instance in the md+ topbar
 * (wide pill trigger, owns the ⌘K hotkey) and the "mobile" instance in the
 * MobileAppBar trailing slot (icon trigger, no hotkey so ⌘K never opens
 * two dialogs).
 */
export function GlobalSearch({
  variant = "desktop",
  dealershipId,
}: {
  variant?: "desktop" | "mobile";
  dealershipId: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const zoneFor = useRooftopZone();
  const navigate = useNavigate();

  // Publish ⌘K into the keyboard cheat-sheet (the `?` overlay reads the
  // registry). Only the desktop instance owns it; the mobile array is empty
  // so the entry never double-lists. Dispatch stays on the local keydown
  // below — the registry deliberately ignores Cmd-combos, so this binding is
  // display-only (opening is idempotent even if that ever changes).
  useHotkeys(
    variant === "desktop"
      ? [
          {
            keys: "⌘K",
            scope: "Global",
            description: "Search Lumika",
            onTrigger: () => setOpen(true),
          },
        ]
      : [],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // ⌘K / Ctrl+K global hotkey — desktop instance only, so the two
  // AppShell instances never open two stacked dialogs.
  useEffect(() => {
    if (variant !== "desktop") return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [variant]);

  // Clear the field on close so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
    }
  }, [open]);

  const contactsQuery = useQuery({
    queryKey: ["global-search", "contacts", debounced],
    queryFn: () => bdcApi.contacts({ search: debounced, pageSize: 8, includeAnonymous: true }),
    enabled: open && debounced.length >= 2,
    // Keep the previous page visible while the next query resolves so the
    // list never collapses to an empty sliver between debounced keystrokes.
    placeholderData: keepPreviousData,
  });

  const leadsQuery = useQuery({
    queryKey: ["global-search", "leads", debounced],
    queryFn: () => bdcApi.leadDirectory({ search: debounced, pageSize: 6 }),
    enabled: open && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const tasksQuery = useQuery({
    queryKey: ["global-search", "tasks", debounced],
    queryFn: () =>
      tasksApi.list({
        view: "store",
        search: debounced,
        createdBy: "all",
        pageSize: 6,
      }),
    enabled: open && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const inventoryQuery = useQuery({
    queryKey: ["global-search", "inventory", dealershipId, debounced],
    queryFn: () =>
      vehiclesApi.search(dealershipId, {
        query: debounced,
        filters: {},
        pagination: { skip: 0, limit: 6 },
        facets: false,
      }),
    enabled: open && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const appointmentsQuery = useQuery({
    queryKey: ["global-search", "appointments", debounced],
    queryFn: () => bdcApi.appointments(undefined, { search: debounced, pageSize: 6 }),
    enabled: open && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const contacts = contactsQuery.data?.items ?? [];
  const leads = leadsQuery.data?.items ?? [];
  const tasks = tasksQuery.data?.items ?? [];
  const vehicles = inventoryQuery.data?.vehicles ?? [];
  const appointments = appointmentsQuery.data?.items ?? [];
  const isFetching =
    contactsQuery.isFetching ||
    leadsQuery.isFetching ||
    tasksQuery.isFetching ||
    inventoryQuery.isFetching ||
    appointmentsQuery.isFetching;
  const hasQuery = debounced.length >= 2;
  const normalizedQuery = debounced.toLowerCase();
  const settings = hasQuery
    ? SETTINGS_DESTINATIONS.filter((item) =>
        `${item.label} ${item.detail}`.toLowerCase().includes(normalizedQuery),
      )
    : [];

  function selectContact(id: string) {
    setOpen(false);
    navigate(`/contacts/${id}`);
  }

  function selectLead(id: string) {
    setOpen(false);
    navigate(`/inbox/${id}`);
  }

  function selectTask(id: string) {
    setOpen(false);
    navigate(`/tasks?task=${id}`);
  }

  // The calendar consumes ?appointmentId= by fetching that one appointment,
  // so this opens any result, not only one inside the loaded window.
  function selectAppointment(id: string) {
    setOpen(false);
    navigate(`/calendar?appointmentId=${id}`);
  }

  function selectVehicle(vin: string) {
    setOpen(false);
    navigate(`/inventory/${encodeURIComponent(vin)}`);
  }

  function selectDestination(to: string) {
    setOpen(false);
    navigate(to);
  }

  return (
    <>
      {variant === "desktop" ? (
        /* Search trigger — a quiet pill sized to the topbar rhythm (h-8). */
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search (⌘K)"
          className={cn(
            "hidden md:flex h-8 items-center gap-2 rounded-md border border-input bg-muted/50 px-3",
            "text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "min-w-[180px] max-w-[280px]",
          )}
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="ml-auto rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </button>
      ) : (
        /* Mobile icon-only trigger */
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search"
          className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Search className="h-5 w-5" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          bare
          // Title-only surface — acknowledge the absent description so Radix
          // doesn't warn (the sr-only title names it for AT).
          aria-describedby={undefined}
          className={cn(
            // Top-anchor + widen over the base centered card.
            "sm:top-[18%] sm:translate-y-0 sm:max-w-xl",
            "rounded-xl sm:rounded-xl overflow-hidden",
            // The base bottom-sheet→center entrance bakes a 48%-of-height
            // vertical compensator tuned for the -translate-y-1/2 rest spot.
            // Now that we top-anchor (translate-y-0) that 48% would play as a
            // literal 48%-of-height slide — the diagonal motion the design
            // bans. Shrink it to a quiet 8px settle; `!` is the only lever
            // that beats a tailwindcss-animate class twMerge can't dedupe.
            "sm:data-[state=open]:!slide-in-from-top-2 sm:data-[state=closed]:!slide-out-to-top-2",
          )}
        >
          <DialogTitle className="sr-only">Search Lumika</DialogTitle>
          <Command shouldFilter={false} className="flex flex-col">
            {/* Input row — 16px gutter, borderless field, inline fetch spinner. */}
            <div className="flex h-12 items-center gap-2 border-b px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Search contacts, leads, appointments, inventory, tasks, and settings…"
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
              {isFetching && (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground motion-reduce:hidden" />
              )}
            </div>

            <Command.List className="max-h-[320px] overflow-y-auto overflow-x-hidden p-1.5">
              {!hasQuery ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Type at least 2 characters to search.
                </p>
              ) : (
                <>
                  {/* Only after a resolved empty result — never mid-fetch, where
                      keepPreviousData may still be showing the last page. */}
                  {!isFetching &&
                    contacts.length === 0 &&
                    leads.length === 0 &&
                    tasks.length === 0 &&
                    vehicles.length === 0 &&
                    appointments.length === 0 &&
                    settings.length === 0 && (
                    <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
                      No results for &ldquo;{query}&rdquo;.
                    </Command.Empty>
                  )}
                  {contacts.length > 0 && (
                    <Command.Group
                      heading="Contacts"
                      className={cn(
                        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5",
                        "[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium",
                        "[&_[cmdk-group-heading]]:font-medium",
                        "[&_[cmdk-group-heading]]:text-muted-foreground",
                      )}
                    >
                      {contacts.map((contact) => {
                        const name = contactDisplayName(contact);
                        return (
                          <Command.Item
                            key={contact.id}
                            value={contact.id}
                            onSelect={() => selectContact(contact.id)}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-foreground">
                              {(name[0] ?? "?").toUpperCase()}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                              {name}
                            </span>
                            {contact.primaryPhone && (
                              <span className="shrink-0 truncate tabular-nums text-xs text-muted-foreground">
                                {contact.primaryPhone}
                              </span>
                            )}
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  )}
                  {leads.length > 0 && (
                    <Command.Group
                      heading="Leads"
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {leads.map((lead) => {
                        const name =
                          lead.displayName ??
                          lead.contactName ??
                          lead.fallbackLabel ??
                          "Unnamed lead";
                        return (
                          <Command.Item
                            key={lead.id}
                            value={`lead-${lead.id}`}
                            onSelect={() => selectLead(lead.id)}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                              <Target className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-foreground">{name}</span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {lead.interest ?? `${lead.status} lead`}
                              </span>
                            </span>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  )}
                  {tasks.length > 0 && (
                    <Command.Group
                      heading="Tasks"
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {tasks.map((task) => (
                        <Command.Item
                          key={task.id}
                          value={`task-${task.id}`}
                          onSelect={() => selectTask(task.id)}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                            <ListTodo className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-foreground">{task.title}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {task.contactName ?? task.leadSummary ?? "Task"}
                            </span>
                          </span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}
                  {appointments.length > 0 && (
                    <Command.Group
                      heading="Appointments"
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {appointments.map((appointment) => (
                        <Command.Item
                          key={appointment.id}
                          value={`appointment-${appointment.id}`}
                          onSelect={() => selectAppointment(appointment.id)}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                            <CalendarClock className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-foreground">
                              {appointment.contactName}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {/* Store time, like every other appointment surface. */}
                              {formatDateTimeInZone(
                                appointment.scheduledStartAt,
                                zoneFor(appointment.rooftopId),
                              )}{" "}
                              · {humanize(appointment.appointmentType)}
                            </span>
                          </span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}
                  {vehicles.length > 0 && (
                    <Command.Group
                      heading="Inventory"
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {vehicles.map((vehicle) => {
                        const description = [
                          vehicle.year,
                          vehicle.make,
                          vehicle.model,
                          vehicle.trim,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <Command.Item
                            key={vehicle.id}
                            value={`vehicle-${vehicle.id}`}
                            onSelect={() => selectVehicle(vehicle.vin)}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                              <Car className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-foreground">
                                {description || vehicle.vin}
                              </span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {vehicle.stock ? `Stock ${vehicle.stock} · ` : ""}
                                VIN {vehicle.vin}
                              </span>
                            </span>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  )}
                  {settings.length > 0 && (
                    <Command.Group
                      heading="Settings"
                      className={cn(
                        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5",
                        "[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium",
                        "[&_[cmdk-group-heading]]:font-medium",
                        "[&_[cmdk-group-heading]]:text-muted-foreground",
                      )}
                    >
                      {settings.map((item) => (
                        <Command.Item
                          key={item.to}
                          value={`settings-${item.to}`}
                          onSelect={() => selectDestination(item.to)}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-accent"
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                            <Settings className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-foreground">{item.label}</span>
                            <span className="block text-xs text-muted-foreground">{item.detail}</span>
                          </span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}
                </>
              )}
            </Command.List>

            {/* Hint bar — desktop only; the mobile bottom sheet has no keyboard. */}
            <div className="hidden items-center gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground sm:flex">
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium">↵</kbd>
                open
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium">esc</kbd>
                close
              </span>
            </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
